import { CreateRoleRequestPermissionsItem } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

export type RolePermission = CreateRoleRequestPermissionsItem;

/** Owner-only: the backend never lets a custom role hold it. */
const OWNER_ONLY: ReadonlySet<string> = new Set(['org.write']);

const ALL_PERMISSIONS: readonly RolePermission[] = Object.values(
  CreateRoleRequestPermissionsItem,
).filter((p) => !OWNER_ONLY.has(p));

export type PermissionGroupKey = 'organisation' | 'catalog' | 'sales' | 'marketing' | 'data';

const GROUP_OF: Readonly<Record<string, PermissionGroupKey>> = {
  org: 'organisation',
  stores: 'organisation',
  settings: 'organisation',
  staff: 'organisation',
  api_keys: 'organisation',
  payments: 'organisation',
  webhooks: 'organisation',
  audit: 'organisation',
  docs: 'organisation',
  products: 'catalog',
  attributes: 'catalog',
  collections: 'catalog',
  media: 'catalog',
  inventory: 'catalog',
  configurator: 'catalog',
  orders: 'sales',
  fulfillments: 'sales',
  returns: 'sales',
  customers: 'sales',
  discounts: 'marketing',
  gift_cards: 'marketing',
  content: 'marketing',
  reviews: 'marketing',
  notifications: 'marketing',
  reports: 'data',
  imports: 'data',
  exports: 'data',
};

const GROUP_ORDER: readonly PermissionGroupKey[] = [
  'organisation',
  'catalog',
  'sales',
  'marketing',
  'data',
];

const groupOf = (permission: string): PermissionGroupKey =>
  GROUP_OF[permission.split('.')[0] ?? ''] ?? 'data';

/** Every permission a custom role may hold, grouped for the checklist. */
export function permissionGroups(): Array<{
  key: PermissionGroupKey;
  permissions: RolePermission[];
}> {
  return GROUP_ORDER.map((key) => ({
    key,
    permissions: ALL_PERMISSIONS.filter((p) => groupOf(p) === key),
  })).filter((group) => group.permissions.length > 0);
}

/** Nobody hands out more than they hold; owners hold everything (`*`). */
export function canGrant(held: readonly string[], permission: string): boolean {
  if (OWNER_ONLY.has(permission)) return false;
  return held.includes('*') || held.includes(permission);
}

export function isRolePermission(value: string): value is RolePermission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

/** "Stripe finance" → "stripe_finance"; the backend wants a lowercase key starting with a letter. */
export function roleKeyFrom(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 60);
}

export interface RoleErrorTarget {
  field: 'key' | 'permissions' | 'root.server';
  key: string;
  values?: Record<string, unknown>;
}

/** Where a role save or delete refusal lands, by the backend's codes. */
export function roleErrorTarget(error: unknown): RoleErrorTarget | null {
  if (!(error instanceof ApiError)) return null;
  switch (error.code) {
    case 'ALREADY_EXISTS':
      return { field: 'key', key: 'team:roles.errors.keyTaken' };
    case 'RESOURCE_IN_USE':
      return {
        field: 'root.server',
        key: 'team:roles.errors.inUse',
        values: { count: typeof error.meta.members === 'number' ? error.meta.members : 1 },
      };
    case 'LIMIT_EXCEEDED':
      return {
        field: 'root.server',
        key: 'team:roles.errors.limit',
        values: { limit: typeof error.meta.limit === 'number' ? error.meta.limit : 50 },
      };
    case 'PERMISSION_DENIED':
      return { field: 'permissions', key: 'team:roles.errors.beyondOwn' };
    case 'CONFLICT':
      return { field: 'root.server', key: 'team:roles.errors.system' };
    default:
      return null;
  }
}
